<template>
  <div class="relative flex flex-1 flex-col">
    <label
      class="mb-1.5 inline-block font-sans text-base text-black lg:mb-3 lg:text-lg lg:leading-snug"
      :class="styleLabel"
    >
      {{ label }}
      <span v-if="required">*</span>
    </label>
    <div class="relative">
      <!--:disabled-dates="disabledDates"-->
      <VueDatePicker
        v-model="value"
        :min-date="props.minDate"
        :teleport="true"
        :action-row="{ showSelect: false, showCancel: false, showNow: false, showPreview: false }"
        :hide-navigation="['time']"
        :day-names="[t('Mon'), t('Tue'), t('Wed'), t('Thu'), t('Fri'), t('Sat'), t('Sun')]"
        @date-update="handleDate"
        @cleared="handleClear"
        :format="format"
        :input-class-name="
          twMerge(
            'h-[47px] lg:h-[60px] flex-1 !rounded-md border-transparent p-3 leading-none focus:border-transparent focus:ring-0 lg:p-4 lg:!text-xl lg:!leading-none',
            styleInput,
          )
        "
      />

      <div
        v-if="v$?.$invalid && v$?.$dirty && v$?.$errors?.length > 0"
        class="absolute mt-0.5 flex w-full flex-col items-start rounded px-1 py-0 pr-3 font-sans text-sm text-pink-act"
        :class="styleError"
        role="alert"
      >
        <span v-for="err in v$.$errors" :key="err.$uid">{{ err.$message }}</span>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
// https://vue3datepicker.com/installation/
import { computed, onMounted, defineAsyncComponent } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Validation } from '@vuelidate/core'

import { useVModel } from '@vueuse/core'

import type { ModelValue } from '@vuepic/vue-datepicker'
import { format as formatDate } from 'date-fns'
import { twMerge } from 'tailwind-merge'
import { ensureDatePickerCss } from '../../libs/datePickerCss'

const VueDatePicker = defineAsyncComponent(() =>
  import('@vuepic/vue-datepicker').then(module => module.default),
)

interface Props {
  date: ModelValue
  label?: string
  styleInput?: string
  styleLabel?: string
  required?: boolean
  minDate?: string | undefined
  styleError?: string
  format?: string
  v$?: Validation
}

const { t } = useI18n()

const props = withDefaults(defineProps<Props>(), {
  format: 'yyyy-MM-dd',
  minDate: undefined,
})

const emit = defineEmits(['update:date'])

const modelDate = useVModel(props, 'date', emit)

const handleDate = (date: Date) => {
  modelDate.value = formatDate(date, props.format)
}

const handleClear = () => {
  modelDate.value = ''
}

const value = computed(() => modelDate.value)

const disabledDates = computed(() => {
  const today = new Date()

  const tomorrow = new Date(today)
  tomorrow.setDate(tomorrow.getDate() + 1)

  const afterTomorrow = new Date(tomorrow)
  afterTomorrow.setDate(tomorrow.getDate() + 1)

  const afterAfterTomorrow = new Date(afterTomorrow)
  afterAfterTomorrow.setDate(afterTomorrow.getDate() + 1)

  return [tomorrow, afterTomorrow, afterAfterTomorrow]
})

onMounted(async () => {
  ensureDatePickerCss()
})
</script>

<style>
.dp__theme_light {
  --dp-background-color: #fff;
  --dp-text-color: #212121;
  --dp-hover-color: #f3f3f3;
  --dp-hover-text-color: #212121;
  --dp-hover-icon-color: #959595;
  --dp-primary-color: #00b4d2;
  --dp-primary-disabled-color: #6bacea;
  --dp-primary-text-color: #f8f5f5;
  --dp-secondary-color: #d8d8d8;
  --dp-border-color: #0088a4;
  --dp-menu-border-color: #0088a4;
  --dp-border-color-hover: #aaaeb7;
  --dp-disabled-color: #f6f6f6;
  --dp-scroll-bar-background: #f3f3f3;
  --dp-scroll-bar-color: #959595;
  --dp-success-color: #76d275;
  --dp-success-color-disabled: #a3d9b1;
  --dp-icon-color: #959595;
  --dp-danger-color: #ff6f60;
  --dp-marker-color: #ff6f60;
  --dp-tooltip-color: #fafafa;
  --dp-disabled-color-text: #8e8e8e;
  --dp-highlight-color: rgb(25 118 210 / 10%);
  --dp-range-between-dates-background-color: var(--dp-hover-color, #f3f3f3);
  --dp-range-between-dates-text-color: var(--dp-hover-text-color, #212121);
  --dp-range-between-border-color: var(--dp-hover-color, #f3f3f3);
  --dp-cell-border-radius: 8px;

  --dp-font-size: 15px;
  /* --dp-font-family: 'Nunito Sans'; */

  --dp-menu-padding: 8px 16px 4px 16px;
}

.dp__menu {
  border-radius: 16px;
  box-shadow: 0px 0px 15px 0px rgba(0, 180, 210, 0.23);
}
.dp__today {
  border: transparent;
  font-weight: bold;
}
.dp__active_date {
  font-weight: bold;
}
.dp__calendar_header_separator {
  display: none;
}
.dp__calendar_header {
  font-size: 11px;
  font-weight: 600;
  color: #929292;
}

.dp__calendar_header_item {
  display: flex;
  justify-content: center;
  align-items: center;
}

.dp__arrow_top {
  display: none;
}
.dp__action_row {
  display: none;
}

.dp__cell_disabled {
  text-decoration: line-through;
  color: #929292;
}
.dp__pointer {
  color: #053a41;
}
.dp__pointer.dp__cell_offset {
  color: #d8d8d8;
}
.dp__pointer.dp__active_date {
  color: #fff;
}
</style>
